/**
 * Error surfacing for the Payment-methods writes (DNKADM-0091, §E), on top of the shared
 * `classifyFormError` (DNKADM-0440): a `400` inline, anything else (an upstream/`502`, a
 * `403`) a toast. The one feature-specific rule: a **`409 CONFLICT`** (a duplicate label)
 * surfaces INLINE defaulting to the spec's "That label is taken." copy when the server
 * sends no message.
 *
 * The PROTECTED-Cash delete `400` is handled separately by the list screen's delete
 * handler (a graceful toast, not a dialog), so this classifier need only cover the
 * create/rename dialog path.
 */
import { ApiError } from '@/api/errors';
import { classifyFormError, type FormErrorView } from '@/api/screenError';
import { serverMessage } from '@/api/serverMessage';

export type PaymentMethodsErrorView = FormErrorView;

/**
 * Classify a failed payment-method create/rename. A `CONFLICT` (`409`, a duplicate
 * label) becomes an inline error defaulting to "That label is taken."; a
 * `VALIDATION` (`400`) becomes an inline error preferring the server's message;
 * everything else becomes an error toast. `fallback` is the field-level default copy.
 */
export function classifyPaymentMethodsError(
  error: unknown,
  fallback: string,
): PaymentMethodsErrorView {
  if (error instanceof ApiError && error.code === 'CONFLICT') {
    return { kind: 'inline', message: serverMessage(error) ?? 'That label is taken.' };
  }
  return classifyFormError(error, fallback);
}
